import { getGPUTier } from "detect-gpu";
import { useState, useEffect } from "react";

export type PerformanceLevel = "low" | "medium" | "high";

/**
 * Performance Detection and Management System
 *
 * This system automatically detects device performance at app startup and provides
 * configuration values that can be used throughout the application to optimize
 * rendering, object limits, and other performance-critical settings.
 *
 * @example Basic Usage in Game Code:
 * ```typescript
 * import { getCurrentPerformanceConfig } from "../../utils/performance";
 *
 * class GameCore {
 *   Update() {
 *     const config = getCurrentPerformanceConfig();
 *
 *     // Limit bullets based on device performance
 *     if (this.bullets.length > config.particleLimit) {
 *       this.bullets.splice(0, this.bullets.length - config.particleLimit);
 *     }
 *
 *     // Adjust enemy count
 *     if (this.enemies.length > config.enemiesLimit) {
 *       // Remove excess enemies
 *     }
 *   }
 * }
 * ```
 *
 * @example Usage in React Components:
 * ```typescript
 * import { usePerformance } from "../../utils/performance";
 *
 * function GameSettings() {
 *   const { performanceLevel, config, updatePerformanceLevel } = usePerformance();
 *
 *   return (
 *     <div>
 *       <p>Current Performance: {performanceLevel}</p>
 *       <p>Max Enemies: {config.enemiesLimit}</p>
 *       <button onClick={() => updatePerformanceLevel('high')}>
 *         Set High Performance
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example Performance-Based Feature Toggles:
 * ```typescript
 * const config = getCurrentPerformanceConfig();
 *
 * // Enable/disable features based on performance
 * if (config.enablePostProcessing) {
 *   enableGlowEffects();
 * }
 *
 * if (config.enableAntialiasing) {
 *   renderer.antialias = true;
 * }
 *
 * // Set animation frame rates
 * this.game.loop.targetFps = config.animationFPS;
 * ```
 */

// Simplified device performance detection focusing on GPU and memory for WebGL games
export async function detectDevicePerformance(): Promise<PerformanceLevel> {
  try {
    // Get device characteristics
    const deviceInfo = await getDeviceInfo();

    // Run only GPU and memory benchmarks with timeout
    const benchmarks = await Promise.race([
      Promise.all([
        measureGPUPerformanceWithDetectGpu(),
        measureMemoryCapabilities(),
      ]),
      // Timeout after 8 seconds
      new Promise<number[]>((_, reject) =>
        setTimeout(() => reject(new Error("Benchmark timeout")), 8000)
      ),
    ]);

    // Calculate weighted scores with emphasis on GPU for WebGL games
    const gpuScore = benchmarks[0];
    const memoryScore = benchmarks[1];

    // Weighted calculation for WebGL games: GPU 70%, Memory 30%
    const rawScore =
      gpuScore * 0.7 + // GPU - Most critical for WebGL rendering
      memoryScore * 0.3; // Memory - Important for textures and assets

    // Device-specific adjustments
    let deviceBonus = 0;
    let devicePenalty = 0;

    // Desktop devices get significant bonus
    if (!deviceInfo.isMobile) {
      deviceBonus += 3; // Desktop bonus
    } else {
      // iOS devices special handling (often don't report specs properly)
      if (deviceInfo.isIOS) {
        // Modern iOS devices have excellent performance but limited API access
        const isModernIOS = deviceInfo.isModernIOS;

        if (isModernIOS) {
          deviceBonus += 2; // Modern iPhone/iPad bonus (iPhone 12+, iPad Air 4+)
        } else {
          deviceBonus += 1; // Older but still capable iOS devices
        }

        console.log("iOS Device detected:", {
          userAgent: deviceInfo.userAgent,
          isModernIOS,
          deviceBonus,
        });
      } else {
        // Android device adjustments - less harsh for modern phones

        // Only penalize truly low-end mobile specs
        if (
          deviceInfo.cores <= 2 &&
          deviceInfo.memory &&
          deviceInfo.memory <= 2
        ) {
          devicePenalty += 2; // Only penalize if both CPU and RAM are very low
        }

        // Bonus for modern flagship devices with high specs (more restrictive)
        if (
          deviceInfo.cores >= 8 &&
          deviceInfo.memory &&
          deviceInfo.memory >= 6
        ) {
          // Make sure it's not a budget device that happens to have decent specs
          const isBudgetDevice = isLowEndDevice(deviceInfo);
          if (!isBudgetDevice) {
            deviceBonus += 1; // True flagship mobile bonus
          }
        }
      }

      // Battery penalty only for critically low battery
      if (
        deviceInfo.batteryLevel &&
        deviceInfo.batteryLevel < 0.1 && // Very critically low (10%)
        !deviceInfo.isCharging
      ) {
        devicePenalty += 1; // Critical battery penalty
      }
    }

    // Check for known low-end patterns (more specific to avoid false positives)
    if (isLowEndDevice(deviceInfo)) {
      devicePenalty += 2; // Reduced penalty, only for clearly old devices
    }

    // Calculate final score with adjustments
    const finalScore = Math.max(1, rawScore + deviceBonus - devicePenalty);

    console.log("Performance Detection:", {
      deviceInfo: {
        isMobile: deviceInfo.isMobile,
        isIOS: deviceInfo.isIOS,
        isModernIOS: deviceInfo.isModernIOS,
        cores: deviceInfo.cores,
        memory: deviceInfo.memory,
        userAgent: deviceInfo.userAgent.substring(0, 100) + "...",
        isLowEndDevice: isLowEndDevice(deviceInfo),
      },
      benchmarks: { gpuScore, memoryScore },
      deviceBonus,
      devicePenalty,
      rawScore,
      finalScore,
    });

    // iOS-specific thresholds (stricter to get realistic iPhone classification)
    if (deviceInfo.isIOS) {
      if (finalScore >= 7.5) return "high"; // Much higher threshold for iOS high-end
      if (finalScore >= 5.0) return "medium"; // Higher threshold for iOS mid-range
      return "low";
    }
    // Mobile-specific thresholds (much stricter for Android)
    else if (deviceInfo.isMobile) {
      if (finalScore >= 10.0) return "high"; // Much stricter threshold for Android high-end
      if (finalScore >= 7.0) return "medium"; // Much stricter threshold for Android mid-range
      return "low";
    } else {
      // Desktop thresholds (strictest)
      if (finalScore >= 9.0) return "high"; // Very high threshold for desktops
      if (finalScore >= 6.5) return "medium"; // Higher threshold for desktop mid-range
      return "low";
    }
  } catch (error) {
    console.warn("Performance detection failed, using fallback:", error);
    return getPerformanceLevelFallback();
  }
}

// Get comprehensive device information
async function getDeviceInfo() {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile =
    /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);

  let batteryInfo = { level: undefined, charging: undefined };
  try {
    const battery = await (navigator as any).getBattery?.();
    if (battery) {
      batteryInfo = { level: battery.level, charging: battery.charging };
    }
  } catch (e) {
    // Battery API not available
  }

  return {
    cores: navigator.hardwareConcurrency || 2,
    memory: (navigator as any).deviceMemory,
    isMobile,
    platform: navigator.platform,
    userAgent,
    batteryLevel: batteryInfo.level,
    isCharging: batteryInfo.charging,
    pixelRatio: window.devicePixelRatio || 1,
    screenSize: { width: screen.width, height: screen.height },
    maxTouchPoints: navigator.maxTouchPoints || 0,
    isIOS: /ipad|iphone|ipod/i.test(userAgent),
    isModernIOS: detectModernIOS(userAgent),
  };
}

// Detect modern iOS devices that should have high performance
function detectModernIOS(userAgent: string): boolean {
  const ua = userAgent.toLowerCase();

  // Modern iPhone detection (iPhone 12 and newer have excellent performance)
  if (/iphone/.test(ua)) {
    // Check for iPhone model patterns
    // iPhone 12+ series (2020+)
    if (/iphone 1[2-9]|iphone [2-9]\d/.test(ua)) return true;

    // iOS version-based detection (iOS 14+ typically runs on iPhone 8+)
    if (/os 1[4-9]_|os [2-9]\d_/.test(ua)) return true;

    // Screen resolution-based detection for modern iPhones
    if (screen.height >= 2436 || screen.width >= 1125) return true; // iPhone X+ resolution
  }

  // Modern iPad detection (iPad Air 4+ and iPad Pro)
  if (/ipad/.test(ua)) {
    // iPad Pro or Air 4+ (2020+)
    if (/ipad pro|ipad air/.test(ua)) return true;

    // iOS version-based detection
    if (/os 1[4-9]_|os [2-9]\d_/.test(ua)) return true;
  }

  return false;
}

// Detect known low-end device patterns (more specific to avoid false positives)
function isLowEndDevice(deviceInfo: any): boolean {
  const ua = deviceInfo.userAgent;

  // Low-end and budget Android patterns
  const lowEndPatterns = [
    { pattern: /android.*go/i, name: "Android Go" },
    { pattern: /android [4-5]\./i, name: "Old Android 4-5" },
    { pattern: /android 6\.[0-2]/i, name: "Early Android 6" },
    { pattern: /sm-[a-z][0-2]\d{2}[a-z]?$/i, name: "Samsung low-end" },
    { pattern: /sm-a[0-2]\d/i, name: "Samsung A0x-A2x" },
    { pattern: /redmi.*[a-c]/i, name: "Redmi A/B/C series" },
    { pattern: /redmi \d+[c]/i, name: "Redmi numbered C" },
    { pattern: /nokia [1-4]/i, name: "Nokia 1-4" },
    { pattern: /moto e[4-9]/i, name: "Moto E series" },
    { pattern: /moto g[1-6]\d?/i, name: "Old Moto G" },
    { pattern: /lg-[a-z][1-3]\d{2}/i, name: "LG low-end" },
    { pattern: /lenovo.*a\d{1}0/i, name: "Lenovo A-series" },
    { pattern: /oppo a[1-5]\d/i, name: "Oppo A-series" },
    { pattern: /vivo y[1-2]\d/i, name: "Vivo Y-series" },
  ];

  const matchedPatterns = lowEndPatterns.filter((p) => p.pattern.test(ua));
  const isLowEndPattern = matchedPatterns.length > 0;

  // Very restrictive additional indicators - only flag if multiple red flags
  const hasVeryLowSpecs =
    deviceInfo.isMobile &&
    deviceInfo.cores <= 2 &&
    deviceInfo.memory &&
    deviceInfo.memory <= 1 && // Very low memory (1GB or less)
    deviceInfo.screenSize &&
    deviceInfo.screenSize.width * deviceInfo.screenSize.height < 600000; // Very low resolution

  console.log("Low-end device detection:", {
    userAgent: ua.substring(0, 150),
    matchedPatterns: matchedPatterns.map((p) => p.name),
    isLowEndPattern,
    hasVeryLowSpecs,
    specs: {
      cores: deviceInfo.cores,
      memory: deviceInfo.memory,
      screenRes: deviceInfo.screenSize
        ? deviceInfo.screenSize.width * deviceInfo.screenSize.height
        : "unknown",
    },
  });

  return isLowEndPattern || hasVeryLowSpecs;
}

// GPU performance assessment using detect-gpu package
async function measureGPUPerformanceWithDetectGpu(): Promise<number> {
  try {
    const gpuTier = await getGPUTier();

    console.log("GPU Detection Result:", gpuTier);

    // Check if this is an iOS device for special handling
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /ipad|iphone|ipod/i.test(userAgent);
    const isModernIOS = detectModernIOS(userAgent);

    // iOS devices often get lower scores due to WebGL limitations, adjust accordingly
    if (isIOS) {
      if (isModernIOS) {
        // Modern iOS devices have good performance, but don't over-boost
        return Math.max(gpuTier.tier === 0 ? 5 : 6, gpuTier.tier * 2); // Moderate boost for modern iOS
      } else {
        // Older iOS devices get modest boost
        return Math.max(gpuTier.tier === 0 ? 3 : 4, gpuTier.tier * 1.5); // Small boost for older iOS
      }
    }

    // Convert detect-gpu tier (0-3) to our scoring system (1-10) for non-iOS
    switch (gpuTier.tier) {
      case 0:
        return 1; // No WebGL or blocked GPU
      case 1:
        return 3; // Low-end GPU (>= 15 fps)
      case 2:
        return 6; // Mid-range GPU (>= 30 fps)
      case 3:
        return 9; // High-end GPU (>= 60 fps)
      default:
        return 1; // Fallback
    }
  } catch (error) {
    console.warn("GPU detection failed:", error);

    // Fallback: Give iOS devices benefit of the doubt
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /ipad|iphone|ipod/i.test(userAgent);
    const isModernIOS = isIOS && detectModernIOS(userAgent);

    if (isModernIOS) return 7; // Modern iOS fallback
    if (isIOS) return 5; // Older iOS fallback
    return 1; // Non-iOS fallback
  }
}

// Memory assessment focused on graphics and game assets
function measureMemoryCapabilities(): Promise<number> {
  return new Promise((resolve) => {
    // Timeout after 2 seconds
    const timeout = setTimeout(() => resolve(1), 2000);

    try {
      let score = 1; // Start lower

      // Hardware concurrency affects parallel asset processing
      const cores = navigator.hardwareConcurrency || 2;
      if (cores >= 8) score += 2; // High-end
      else if (cores >= 6) score += 1.5; // Mid-high
      else if (cores >= 4) score += 1; // Mid-range
      // Low-end gets no bonus

      // Device memory - critical for texture loading and game assets
      const memory = (navigator as any).deviceMemory;
      if (memory) {
        if (memory >= 8) score += 5; // 8GB+ = high-end, plenty for textures
        else if (memory >= 6) score += 3; // 6GB = mid-high
        else if (memory >= 4) score += 2; // 4GB = mid-range
        else if (memory >= 2) score += 0.5; // 2GB = low-mid
        else score -= 1; // Less than 2GB = penalty for games
      }

      // Memory stress test for texture/asset loading simulation
      try {
        const testSize = 3000000; // 3M elements = ~12MB (typical texture size)
        const testArray = new Float32Array(testSize);

        // Simulate texture data processing
        for (let i = 0; i < testSize; i += 3000) {
          testArray[i] = Math.random();
        }

        // Quick processing check
        let sum = 0;
        for (let i = 0; i < testSize; i += 6000) {
          sum += testArray[i];
        }

        if (sum > 0) score += 1.5; // Successfully handled texture-like allocation
      } catch (error) {
        score -= 2; // Failed allocation = significant penalty for games
      }

      clearTimeout(timeout);
      resolve(Math.min(Math.max(score, 1), 10));
    } catch (error) {
      clearTimeout(timeout);
      resolve(1);
    }
  });
}

// Fallback detection for when benchmarks fail
function getPerformanceLevelFallback(): PerformanceLevel {
  const cores = navigator.hardwareConcurrency || 2;
  const memory = (navigator as any).deviceMemory || 4;
  const userAgent = navigator.userAgent.toLowerCase();

  // Check for mobile
  const isMobile =
    /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);

  // Desktop devices default to high unless very old
  if (!isMobile) {
    if (cores >= 2) return "high"; // Most desktops should be high
    return "medium"; // Very old desktops
  }

  // Check for known low-end mobile patterns
  const isLowEnd =
    /android.*go|android [4-6]\.|sm-[a-z]\d{3}[a-z]?$|redmi.*[a-c]|nokia [1-5]|moto e\d/i.test(
      userAgent
    );

  if (isLowEnd || (cores <= 2 && memory <= 2)) {
    return "low";
  }

  if (cores >= 6 && memory >= 6) {
    return "high";
  }

  return "medium";
}

// Get current performance setting with modern detection
export async function getPerformanceLevel(): Promise<PerformanceLevel> {
  const saved = localStorage.getItem("device-performance");
  if (saved && ["low", "medium", "high"].includes(saved)) {
    return saved as PerformanceLevel;
  }
  return await detectDevicePerformance();
}

// Synchronous version for immediate use (falls back to basic detection)
export function getPerformanceLevelSync(): PerformanceLevel {
  const saved = localStorage.getItem("device-performance");
  if (saved && ["low", "medium", "high"].includes(saved)) {
    return saved as PerformanceLevel;
  }
  return getPerformanceLevelFallback();
}

// Set performance level
export function setPerformanceLevel(level: PerformanceLevel): void {
  localStorage.setItem("device-performance", level);
}

// Performance configuration based on level
export const PERFORMANCE_CONFIG = {
  low: {
    animationFPS: 30,
    gemsLimit: 50,
    enemiesLimit: 50,
    bulletsLimit: 40,
    coinsLimit: 120,
    minCoinsToKeep: 100, // More aggressive cleanup for low-end devices
    unreachableDistance: 700, // Closer distance considered unreachable
  },
  medium: {
    animationFPS: 45,
    gemsLimit: 100,
    enemiesLimit: 100,
    bulletsLimit: 80,
    coinsLimit: 160,
    minCoinsToKeep: 140, // Balanced cleanup
    unreachableDistance: 800, // Moderate distance
  },
  high: {
    animationFPS: 60,
    gemsLimit: 200,
    enemiesLimit: 150,
    bulletsLimit: 160,
    coinsLimit: 200,
    minCoinsToKeep: 180, // Conservative cleanup for high-end devices
    unreachableDistance: 900, // Larger distance before considering unreachable
  },
} as const;

// Global performance state management
let isInitialized = false;
let isInitializing = false;
let currentPerformanceLevel: PerformanceLevel | null = null;
const performanceChangeListeners: Array<
  (level: PerformanceLevel, isAutoDetected: boolean) => void
> = [];

// Initialize performance detection globally at app startup
export async function initializePerformance(): Promise<PerformanceLevel> {
  if (isInitialized && currentPerformanceLevel) {
    return currentPerformanceLevel;
  }

  if (isInitializing) {
    // If already initializing, wait for it to complete
    return new Promise((resolve) => {
      const checkInitialized = () => {
        if (isInitialized && currentPerformanceLevel) {
          resolve(currentPerformanceLevel);
        } else {
          setTimeout(checkInitialized, 100);
        }
      };
      checkInitialized();
    });
  }

  isInitializing = true;

  try {
    console.log("Initializing performance detection...");

    const saved = localStorage.getItem("device-performance");
    let detectedLevel: PerformanceLevel;
    let isAutoDetected = false;

    if (saved && ["low", "medium", "high"].includes(saved)) {
      console.log("Performance loaded from storage:", saved);
      detectedLevel = saved as PerformanceLevel;
    } else {
      console.log("Running performance detection...");
      detectedLevel = await detectDevicePerformance();
      setPerformanceLevel(detectedLevel);
      isAutoDetected = true;
      console.log("Performance auto-detected:", detectedLevel);
    }

    currentPerformanceLevel = detectedLevel;
    isInitialized = true;
    isInitializing = false;

    // Notify all listeners
    performanceChangeListeners.forEach((listener) => {
      try {
        listener(detectedLevel, isAutoDetected);
      } catch (error) {
        console.error("Error in performance change listener:", error);
      }
    });

    return detectedLevel;
  } catch (error) {
    console.error("Performance initialization failed:", error);
    const fallbackLevel = getPerformanceLevelFallback();
    currentPerformanceLevel = fallbackLevel;
    isInitialized = true;
    isInitializing = false;

    // Notify listeners with fallback
    performanceChangeListeners.forEach((listener) => {
      try {
        listener(fallbackLevel, false);
      } catch (error) {
        console.error("Error in performance change listener:", error);
      }
    });

    return fallbackLevel;
  }
}

// Check if performance has been initialized
export function isPerformanceInitialized(): boolean {
  return isInitialized;
}

// Get current performance level (only works after initialization)
export function getCurrentPerformanceLevel(): PerformanceLevel | null {
  return currentPerformanceLevel;
}

// Get current performance level synchronously, initialize if needed
export function getCurrentPerformanceLevelSync(): PerformanceLevel {
  if (currentPerformanceLevel) {
    return currentPerformanceLevel;
  }

  // Fallback to sync detection if not initialized
  return getPerformanceLevelSync();
}

// Update performance level and notify listeners
export function updatePerformanceLevel(
  level: PerformanceLevel,
  isAutoDetected: boolean = false
): void {
  currentPerformanceLevel = level;
  setPerformanceLevel(level);

  // Notify all listeners
  performanceChangeListeners.forEach((listener) => {
    try {
      listener(level, isAutoDetected);
    } catch (error) {
      console.error("Error in performance change listener:", error);
    }
  });
}

// Add listener for performance level changes
export function addPerformanceChangeListener(
  listener: (level: PerformanceLevel, isAutoDetected: boolean) => void
): void {
  performanceChangeListeners.push(listener);
}

// Remove listener for performance level changes
export function removePerformanceChangeListener(
  listener: (level: PerformanceLevel, isAutoDetected: boolean) => void
): void {
  const index = performanceChangeListeners.indexOf(listener);
  if (index > -1) {
    performanceChangeListeners.splice(index, 1);
  }
}

// Re-run performance detection
export async function redetectPerformance(): Promise<PerformanceLevel> {
  console.log("Re-running performance detection...");

  try {
    const detectedLevel = await detectDevicePerformance();
    currentPerformanceLevel = detectedLevel;
    setPerformanceLevel(detectedLevel);

    // Notify all listeners that this was auto-detected
    performanceChangeListeners.forEach((listener) => {
      try {
        listener(detectedLevel, true);
      } catch (error) {
        console.error("Error in performance change listener:", error);
      }
    });

    return detectedLevel;
  } catch (error) {
    console.error("Performance re-detection failed:", error);
    throw error;
  }
}

// Get performance configuration for current level
export function getCurrentPerformanceConfig() {
  const level = getCurrentPerformanceLevelSync();
  return PERFORMANCE_CONFIG[level];
}

// React hook for using performance state in components
export function usePerformance() {
  const [performanceLevel, setPerformanceLevel] = useState<PerformanceLevel>(
    getCurrentPerformanceLevelSync()
  );
  const [isAutoDetected, setIsAutoDetected] = useState(false);
  const [isInitialized, setIsInitialized] = useState(
    isPerformanceInitialized()
  );

  useEffect(() => {
    const handlePerformanceChange = (
      level: PerformanceLevel,
      autoDetected: boolean
    ) => {
      setPerformanceLevel(level);
      setIsAutoDetected(autoDetected);
      setIsInitialized(true);
    };

    // Add listener for performance changes
    addPerformanceChangeListener(handlePerformanceChange);

    // If performance is already initialized, update state
    if (isPerformanceInitialized()) {
      const currentLevel = getCurrentPerformanceLevel();
      if (currentLevel) {
        setPerformanceLevel(currentLevel);
        setIsInitialized(true);
      }
    }

    // Cleanup listener on unmount
    return () => {
      removePerformanceChangeListener(handlePerformanceChange);
    };
  }, []);

  return {
    performanceLevel,
    isAutoDetected,
    isInitialized,
    config: PERFORMANCE_CONFIG[performanceLevel],
    updatePerformanceLevel: (level: PerformanceLevel) =>
      updatePerformanceLevel(level, false),
    redetectPerformance,
  };
}
